import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as Avatar } from "./IconUmmahfy.svg";

/**
 * Auto-advancing event preview shown next to the hero copy.
 *
 * Reuses your existing `info` shape from Home.js (already filtered
 * for upcoming + approved + non-private). No new API calls.
 *
 * Behavior:
 *  - auto-advances every `intervalMs`
 *  - pauses on hover (desktop) and on touch (mobile)
 *  - supports swipe left/right
 *  - supports keyboard ←/→ when the carousel has focus
 *  - clicking a slide navigates to /event/:id (Link wrapping)
 */
function HeroEventCarousel({
  events = [],
  matchedOrgFor,
  intervalMs = 4500,
  maxSlides = 6,
}) {
  // Cap slides for perf + visual focus.
  const slides = useMemo(
    () => (Array.isArray(events) ? events.slice(0, maxSlides) : []),
    [events, maxSlides]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const containerRef = useRef(null);

  // Reset to first slide if events change.
  useEffect(() => {
    setActiveIndex(0);
  }, [slides.length]);

  const goTo = useCallback(
    (idx) => {
      if (slides.length === 0) return;
      const next = ((idx % slides.length) + slides.length) % slides.length;
      setActiveIndex(next);
    },
    [slides.length]
  );

  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  // Auto-advance.
  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [paused, slides.length, intervalMs]);

  // Keyboard navigation.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [next, prev]);

  if (slides.length === 0) {
    return null;
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
    touchDeltaX.current = 0;
    setPaused(true);
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = (e.touches?.[0]?.clientX ?? 0) - touchStartX.current;
  };

  const handleTouchEnd = () => {
    const delta = touchDeltaX.current;
    if (Math.abs(delta) > 40) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
    // Resume auto-advance shortly after.
    setTimeout(() => setPaused(false), 600);
  };

  return (
    <div
      ref={containerRef}
      className="hero-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured upcoming events"
      tabIndex={0}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="hero-carousel-stage">
        {slides.map((evt, idx) => {
          const isActive = idx === activeIndex;
          const isPrev =
            idx ===
            (activeIndex - 1 + slides.length) % slides.length;
          const isNext = idx === (activeIndex + 1) % slides.length;

          let positionClass = "hero-carousel-slide-hidden";
          if (isActive) positionClass = "hero-carousel-slide-active";
          else if (isNext) positionClass = "hero-carousel-slide-next";
          else if (isPrev) positionClass = "hero-carousel-slide-prev";

          const matched = matchedOrgFor ? matchedOrgFor(evt.organization) : null;
          const dateLabel = evt.timeArray?.[0] || "";
          const tag = Array.isArray(evt.tags) && evt.tags.length > 0 ? evt.tags[0] : null;

          return (
            <Link
              key={evt.event_id || idx}
              to={`/event/${evt.event_id}`}
              className={`hero-carousel-slide ${positionClass}`}
              aria-hidden={!isActive}
              tabIndex={isActive ? 0 : -1}
              draggable={false}
            >
              <div className="hero-carousel-img-wrap img-mask">
                {evt.img ? (
                  <img
                    src={evt.img}
                    alt={evt.name || "Event image"}
                    className="hero-carousel-img"
                    loading={isActive ? "eager" : "lazy"}
                    draggable={false}
                  />
                ) : (
                  <div className="hero-carousel-img hero-carousel-img-placeholder" />
                )}
              </div>
              <div className="hero-carousel-body card-home-right">
                <p className="hero-carousel-event-name event" title={evt.name}>
                  {evt.name}
                </p>
                <div className="hero-carousel-meta masjid">
                  {matched?.profilePicture ? (
                    <img
                      src={matched.profilePicture}
                      alt={`${matched.name} logo`}
                      className="hero-carousel-org-pic home-avatar-icon"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <Avatar className="hero-carousel-org-pic home-avatar-icon" />
                  )}
                  <span className="hero-carousel-org-name masjid-text">
                    {matched?.name || evt.organization || "Ummahfy"}
                  </span>
                </div>
                {/* {dateLabel && (
                  <p className="hero-carousel-date date">{dateLabel}</p>
                )} */}
                {tag && (
                  <div className="hero-carousel-badge-row badge-grp">
                    <div className="badge tag-badge">
                      <p>{tag}</p>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="hero-carousel-nav hero-carousel-nav-prev"
            aria-label="Previous event"
            onClick={(e) => {
              e.preventDefault();
              prev();
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="hero-carousel-nav hero-carousel-nav-next"
            aria-label="Next event"
            onClick={(e) => {
              e.preventDefault();
              next();
            }}
          >
            ›
          </button>

          <div className="hero-carousel-dots" role="tablist">
            {slides.map((_, idx) => (
              <button
                type="button"
                key={idx}
                role="tab"
                aria-selected={idx === activeIndex}
                aria-label={`Go to event ${idx + 1}`}
                className={`hero-carousel-dot ${
                  idx === activeIndex ? "is-active" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  goTo(idx);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default HeroEventCarousel;
